import React, { useState, useEffect } from 'react';
import { getCategories } from '../services/api';
import { getSimplePlaceholder } from '../utils/placeholder';

function CategoriesMegaMenu({ isOpen, onClose, onCategoryFilterChange, categoriesButtonRef }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [arrowPosition, setArrowPosition] = useState('50%');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Calculate arrow position based on Categories button location
  useEffect(() => {
    if (isOpen && categoriesButtonRef?.current) {
      const calculatePosition = () => {
        const button = categoriesButtonRef.current;
        if (!button) return;
        
        const buttonRect = button.getBoundingClientRect();
        const buttonCenterX = buttonRect.left + buttonRect.width / 2;
        const viewportCenter = window.innerWidth / 2;
        const offsetFromCenter = buttonCenterX - viewportCenter;
        
        // Modal is centered, so calculate offset relative to modal width
        // Modal width is 75% with max 1200px, min 900px
        const modalWidth = Math.min(Math.max(window.innerWidth * 0.75, 900), 1200);
        const offsetInPixels = offsetFromCenter;
        
        // Convert to percentage offset from center (50%)
        // left: 50% means center, so we add the offset
        const offsetPercent = (offsetInPixels / modalWidth) * 100;
        const arrowLeft = `calc(50% + ${offsetPercent}%)`;
        
        setArrowPosition(arrowLeft);
      };
      
      // Calculate on mount and resize
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      
      return () => {
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isOpen, categoriesButtonRef]);

  // Get subcategories from the category data (from API)
  const getSubcategories = (category) => {
    // Use subcategories from API if available
    if (category.subcategories && Array.isArray(category.subcategories) && category.subcategories.length > 0) {
      return category.subcategories.map(sub => ({
        name: sub.name,
        image: sub.image || getSimplePlaceholder(sub.name, 150, 150),
        id: sub.id
      }));
    }
    return [];
  };

  if (!isOpen) return null;

  const subcategories = hoveredCategory ? getSubcategories(hoveredCategory) : [];

  return (
    <div 
      className="fixed z-[100]"
      style={{ 
        top: '110px',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
      onMouseEnter={() => {}} // Keep menu open when hovering over it
      onMouseLeave={(e) => {
        // Add a small delay before closing to allow smooth transitions
        // Check if mouse is moving to the button or within the menu area
        const relatedTarget = e.relatedTarget;
        // Check if relatedTarget is a valid DOM element before calling closest
        if (relatedTarget && relatedTarget.nodeType === Node.ELEMENT_NODE && (
          relatedTarget.closest('.categories-mega-menu') ||
          relatedTarget.closest('[class*="categories-mega-menu"]') ||
          (categoriesButtonRef?.current && categoriesButtonRef.current.contains(relatedTarget))
        )) {
          return; // Don't close if moving within menu or to button
        }
        // Small delay before closing
        setTimeout(() => {
          onClose();
        }, 200);
      }}
    >
      {/* Arrow pointer pointing up to Categories button */}
      <div className="relative w-full flex justify-center">
        <div 
          className="absolute -top-4 z-10"
          style={{
            left: arrowPosition,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '16px solid transparent',
            borderRight: '16px solid transparent',
            borderBottom: '16px solid white',
            filter: 'drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.15))',
          }}
        />
        <div 
          className="absolute -top-[17px] z-0"
          style={{
            left: arrowPosition,
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '17px solid transparent',
            borderRight: '17px solid transparent',
            borderBottom: '17px solid #e5e7eb',
          }}
        />
      </div>
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden"
        style={{ 
          width: '75%',
          maxWidth: '1200px',
          minWidth: '900px',
          margin: '0 auto'
        }}
      >
        <div className="px-6 py-5">
        <div className="flex gap-8">
          {/* Left Side - Categories List */}
          <div className="w-56 flex-shrink-0 border-r border-gray-200 pr-5">
            <nav className="space-y-0.5">
              {loading ? (
                <div className="text-sm text-gray-500 py-4">Loading categories...</div>
              ) : categories.length === 0 ? (
                <div className="text-sm text-gray-500 py-4">No categories available</div>
              ) : (
                categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onCategoryFilterChange) {
                        onCategoryFilterChange(category.id);
                      }
                      onClose();
                    }}
                    onMouseEnter={() => setHoveredCategory(category)}
                    className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                      hoveredCategory?.id === category.id
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{category.name}</span>
                    <svg 
                      className="w-4 h-4 text-gray-400"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))
              )}
            </nav>
          </div>

          {/* Right Side - Subcategories Grid */}
          <div className="flex-1 min-h-[300px]">
            {hoveredCategory ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onCategoryFilterChange) {
                        onCategoryFilterChange(hoveredCategory.id);
                      }
                      onClose();
                    }}
                    className="text-base font-bold text-gray-800 hover:text-primary transition-colors cursor-pointer text-left"
                  >
                    All {hoveredCategory.name}
                  </button>
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (onCategoryFilterChange) {
                        onCategoryFilterChange(hoveredCategory.id);
                      }
                      onClose();
                    }}
                    className="text-sm text-gray-600 hover:text-primary font-medium flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    View All
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                {subcategories.length > 0 ? (
                  <div className="grid grid-cols-4 gap-5">
                    {subcategories.map((subcat, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (onCategoryFilterChange) {
                            onCategoryFilterChange(hoveredCategory.id);
                          }
                          onClose();
                        }}
                        className="flex flex-col items-center group cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 mb-2 group-hover:ring-2 group-hover:ring-primary/50 transition-all duration-200 shadow-sm">
                          <img
                            src={subcat.image || getSimplePlaceholder(150, 150)}
                            alt={subcat.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                            onError={(e) => {
                              e.target.src = getSimplePlaceholder(150, 150);
                            }}
                          />
                        </div>
                        <span className="text-xs text-center text-gray-700 group-hover:text-primary font-medium transition-colors leading-tight">
                          {subcat.name}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 py-8 text-center">
                    No subcategories available for this category
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <p className="text-sm">Hover over a category to see subcategories</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default CategoriesMegaMenu;

